import { NgClass } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, Input } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GameType, GetPresignedUrlResponse } from "@apiserver-rest/ponder-data-client-base";
import { PonderTasksClient } from "@apiserver-rest/ponder-tasks-client";
import { ExternalCollectionType, ImportCollectionRequest, ImportCollectionResponse, TaskResponse } from "@apiserver-rest/ponder-tasks-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { FormatNumberPipe } from "@ponder-shared/pipes/format-number.pipe";
import { take } from "rxjs";
import { ImportCollectionResultsComponent } from "./import-collection-results.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { RateLimitedError } from "@ponder-shared/errors/rate-limited-error";
import { ImportCollectionMagicCsvNoteComponent } from "./notes/import-collection-note.component";
import { AuthSession } from "@ponder-core/auth/auth-session";

@Component({
    imports: [
        NgClass,
        BusySpinComponent,
        FormatNumberPipe,
        ImportCollectionResultsComponent,
        ImportCollectionMagicCsvNoteComponent
    ],
    templateUrl: "./import-collection.component.html"
})
export class ImportCollectionComponent {
    @Input({ required: true }) public username!: string;

    public isOwnCollection: boolean = false;

    public gameTypes = GameType;
    public collectionFormats = ExternalCollectionType;
    public importState: ImportState = { game: GameType.Magic, skipCollected: true, importOther: false, skipCollectedOther: true };
    public validExtensions: string = "*";

    public isImporting: boolean = false;
    public importFailure: boolean = false;
    public failureReason?: string;

    public fileSizeError: boolean = false;
    public fileExtensionError: boolean = false;
    public isThrottled: boolean = false;

    constructor(
        private ponderDataClient: PonderDataClient, 
        private ponderTasksClient: PonderTasksClient, 
        private authService: AuthService, 
        private httpClient: HttpClient,
        private events: EventBroker) {
        
    }

    public ngOnInit(): void {
        this.authService.getSessionAsync().then((authSession?: AuthSession) => {
            if (authSession?.username && (authSession.username == this.username)) {
                this.isOwnCollection = true;
            }
        });
    }

    public resetImportState(): void {
        
    }

    // -------------------------------------------------------------------------------------
    // Game Select
    // -------------------------------------------------------------------------------------

    public onGameSelected(gameType: GameType): void {
        if (this.importState.game && this.importState.game == gameType) {
            return;
        }

        this.importState.game = gameType;
        this.importState.collectionType = undefined;
        this.importState.fileName = undefined;
    }

    public isGameSelected(gameType: GameType): boolean {
        return (this.importState.game == gameType);
    }
    
    // -------------------------------------------------------------------------------------
    // Format Select
    // -------------------------------------------------------------------------------------

    public onFormatSelected(collectionFormat: ExternalCollectionType): void {
        if (this.importState.collectionType && (this.importState.collectionType == collectionFormat)) {
            return;
        }

        this.importState.collectionType = collectionFormat;
        this.validExtensions = CollectionFormatExtensions.get(collectionFormat)!;
    }

    public isFormatSelected(collectionFormat: ExternalCollectionType): boolean {
        return (this.importState.collectionType == collectionFormat);
    }
    
    // -------------------------------------------------------------------------------------
    // File Select
    // -------------------------------------------------------------------------------------

    public onFileSelected(fileInput: HTMLInputElement): void {
        this.fileSizeError = false;
        this.fileExtensionError = false;

        if (!fileInput.files?.length) {
            this.importState.fileName = undefined;
        } else {
            this.importState.file = fileInput.files[0];
            this.importState.fileName = fileInput.files[0].name;
            this.importState.fileBytes = fileInput.files[0].size;
            
            this.validateFileSize();
            this.validateFileExtension();
        }
    }

    private validateFileSize(): void {
        if (this.importState.fileBytes! > 1000000) {
            this.fileSizeError = true;
        }
    }

    private validateFileExtension(): void {
        var nameLower = this.importState.fileName!.toLowerCase();
        var extension = nameLower.substring(nameLower.lastIndexOf("."));
        var validExtensions = this.validExtensions.split(',');

        this.fileExtensionError = true;

        validExtensions.forEach(e => {
            if (e == "*" || extension == e.toLowerCase()) {
                this.fileExtensionError = false;
            }
        });
    }
    
    // -------------------------------------------------------------------------------------
    // Import
    // -------------------------------------------------------------------------------------

    public onImportClicked(): void {
        if (this.isImporting || 
            !this.importState.collectionType || 
            !this.importState.fileName || 
            this.fileSizeError || 
            this.fileExtensionError ||
            this.isThrottled) {
            return;
        }

        this.isImporting = true;
        this.importFailure = false;
        this.failureReason = undefined;

        this.fetchUploadDestination();
    }

    /**
     * Step 1: Get the presigned URL to upload the collection file to.
     */
    private fetchUploadDestination(): void {
        this.ponderDataClient
            .artifactGeneratePresignedUrl(
                this.importState.fileName, 
                "plain/text", 
                this.importState.fileBytes!,
                300)
            .pipe(take(1))
            .subscribe({
                next: (response: GetPresignedUrlResponse) => {
                    if (response.success && response.presignedUrl) {
                        this.importState.objectKey = response.objectKey;
                        this.uploadCollectionArtifact(response.presignedUrl);
                    } else {
                        this.onImportComplete(false, "Failed to retrieve upload destination.");
                    }
                },
                error: () => {
                    this.onImportComplete(false, "Failed to retrieve upload destination.");
                }
            });
    }

    /**
     * Step 2: Upload the collection file to the artifacts bucket.
     * @param destination
     */
    private uploadCollectionArtifact(destination: string): void {
        this.httpClient
            .put(destination, this.importState.file!, {
                headers: {
                    "Content-Type": "plain/text",
                    "Content-Length": this.importState.fileBytes! + ""
                }
            })
            .pipe(take(1))
            .subscribe({
                next: (response: Object) => {
                    this.initiateCollectionImport();
                },
                error: () => {
                    this.onImportComplete(false, "Failed to upload collection artifact.");
                }
            });
    }

    /**
     * Step 3: Kick off the collection import.
     */
    private initiateCollectionImport(): void {
        this.ponderTasksClient
            .userCollectionImport(new ImportCollectionRequest({
                artifactObjectKey: this.importState.objectKey!,
                game: this.importState.game,
                collectionType: this.importState.collectionType!,
                skipCollected: this.importState.skipCollected!,
                importOther: this.importState.importOther!,
                skipOther: this.importState.skipCollectedOther!
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: ImportCollectionResponse) => {
                    if (response.success && response.status) {
                        this.events.collection.collectionImportedEvent.emit(response.status);
                        this.onImportComplete(true);
                    } else {
                        this.onImportComplete(false, response.error ?? "Failed to initiate collection import.");
                    }
                },
                error: (e: any) => {
                    if (e.status == 429) {
                        var rateLimitedError = JSON.parse(e.response) as RateLimitedError;
                        this.isThrottled = true;
                        this.onImportComplete(false, `Slow down! You have reached your hourly limit (${rateLimitedError.limit}) on collection imports.`);
                    } else {
                        this.onImportComplete(false, "Failed to initiate collection import.");
                    }
                }
            });
    }

    /**
     * Step 4: After collection import has been initiated.
     * @param success 
     * @param failureReason 
     */
    private onImportComplete(success: boolean, failureReason?: string): void {
        this.isImporting = false;
        this.importFailure = !success;
        this.failureReason = failureReason;
        this.importState.presignedUrl = undefined;
        this.importState.objectKey = undefined;

        if (success) {
            this.importState.file = undefined;
            this.importState.fileName = undefined;
            this.importState.fileBytes = undefined;
            this.importState.collectionType = undefined;
        }
    }
}

type ImportState = {
    game: GameType,
    collectionType?: ExternalCollectionType,
    skipCollected: boolean,
    importOther: boolean,
    skipCollectedOther: boolean,
    file?: File,
    fileName?: string,
    fileBytes?: number,
    presignedUrl?: string,
    objectKey?: string
};

const CollectionFormatExtensions = new Map<ExternalCollectionType, string>([
    [ ExternalCollectionType.Csv, ".csv" ],
    [ ExternalCollectionType.UrzaGatherer, ".ugs" ]
]);